import { VehicleType } from "../../types";
import { DEFAULT_LOGISTICS_CPT_SLABS } from "../../constants/logisticsCptSlabs";
import type { customFilter } from "../../config/ApiConfig";
import { ConfigurationKeys } from "../../config/ApiConfig";
import type { DispatchDashboardMode } from "./types";

/**
 * Extracts all eligible vehicle types from order items
 * @param orders - Array of order objects
 * @returns Array of numeric vehicle type values
 */
export const getEligibleVehicleTypes = (orders: any[]): number[] => {
  const eligibleVTs: number[] = [];

  orders.forEach((order) => {
    if (order.orderItems && Array.isArray(order.orderItems)) {
      order.orderItems.forEach((item: any) => {
        if (
          typeof item.eligibleVehicleType === "number" &&
          item.eligibleVehicleType > 0
        ) {
          eligibleVTs.push(item.eligibleVehicleType);
        }
      });
    }
  });

  return eligibleVTs;
};

/**
 * Converts numeric vehicle type to VehicleType enum
 * @param vehicleTypeNumber - Numeric vehicle type (2, 3 or 4)
 * @returns VehicleType enum value
 */
export const mapVehicleTypeNumber = (
  vehicleTypeNumber: number
): VehicleType => {
  if (vehicleTypeNumber === 2) return VehicleType.TWO_WHEELER;
  if (vehicleTypeNumber === 3) return VehicleType.THREE_WHEELER;
  if (vehicleTypeNumber === 4) return VehicleType.FOUR_WHEELER;
  return VehicleType.OTHER;
};

/**
 * Calculates suggested vehicle type from multiple orders
 * Returns the maximum eligible vehicle type across all order items
 * @param orders - Array of order objects
 * @returns "TWO_WHEELER" if no eligible types found
 */
export const calculateSuggestedVehicleType = (orders: any[]): VehicleType => {
  const eligibleVTs = getEligibleVehicleTypes(orders);

  // Return TWO_WHEELER if no eligible vehicle types found
  if (eligibleVTs.length === 0) {
    return VehicleType.TWO_WHEELER;
  }

  const maxEligibleVT = Math.max(...eligibleVTs);
  return mapVehicleTypeNumber(maxEligibleVT);
};

export const CPT_SLAB_META: Record<
  string,
  { icon: string; fallbackLabel: string }
> = {
  "1": {
    icon: "🏎️",
    fallbackLabel: DEFAULT_LOGISTICS_CPT_SLABS["1"],
  },
  "2": {
    fallbackLabel: DEFAULT_LOGISTICS_CPT_SLABS["2"],
    icon: "🚚",
  },
  "-2": {
    fallbackLabel: DEFAULT_LOGISTICS_CPT_SLABS["-2"],
    icon: "🚶",
  },
  "-1": {
    fallbackLabel: DEFAULT_LOGISTICS_CPT_SLABS["-1"],
    icon: "❓",
  },
};

export const FASTEST_CPT_SLAB_LEVEL = "1";

/**
 * Default API filters when the host (e.g. OrCA) does not pass customFilters.
 * Mirrors OrCA ApiAwareComponents so local LMD and OrCA stay in sync:
 * - r2d → READY_TO_DISPATCH
 * - dispatched → DISPATCHED
 * Host-provided customFilters always win (never merged over).
 */
export function buildDefaultDispatchCustomFilters(
  dashboardMode: DispatchDashboardMode = "r2d",
): customFilter {
  const orderStatus =
    dashboardMode === "dispatched"
      ? (["DISPATCHED"] as string[])
      : (["READY_TO_DISPATCH"] as string[]);

  const facetBase = {
    orderStatus,
    shippedOrders: "false",
    isPopulated: "true",
    limit: "1",
  };
  const orderBase = {
    orderStatus,
    shippedOrders: "false",
    isPopulated: "true",
    limit: "50",
  };

  return {
    [ConfigurationKeys.getDispatchCustomerFacets]: {
      filters: { ...facetBase, facets: "customers" },
    },
    [ConfigurationKeys.getDispatchSupplierFacets]: {
      filters: { ...facetBase, facets: "suppliers" },
    },
    [ConfigurationKeys.getDispatchRiderFacets]: {
      filters: { ...facetBase, facets: "riders" },
    },
    [ConfigurationKeys.getDispatchOrdersForSupplier]: {
      filters: { ...orderBase },
    },
    // Legacy R2D keys — services fall back to these when Dispatch keys absent
    [ConfigurationKeys.getR2DCustomerFacets]: {
      filters: { ...facetBase, facets: "customers" },
    },
    [ConfigurationKeys.getR2DSupplierFacets]: {
      filters: { ...facetBase, facets: "suppliers" },
    },
    [ConfigurationKeys.getR2DOrdersForSupplier]: {
      filters: { ...orderBase },
    },
  };
}
